// src/pages/(User)salary_Final/components/MissingSalaryState.jsx
// Empty state shown when the employee has no net / gross / insurance salary
// configured — tells them to contact their manager.
//
// Same visual language as LoadErrorState and the session screens: the shared
// session palette (sidebar colour tokens + brand blue), a tinted IconTile,
// the secondary session button, no shadows and no animation.
import React, { useLayoutEffect, useRef, useState } from 'react';
import { Wallet, Award, DollarSign, Shield, UserCheck, RefreshCw } from '../../../constant/icons';
import { getSessionPalette, SessionStyles, IconTile } from '../../../components/session/SessionUi';

const MissingSalaryState = ({ t, theme, isRTL, isMobile, styles }) => {
  const palette = getSessionPalette(theme);
  const isDark = theme !== 'light';

  // Size the card to end exactly at the bottom of the page's scroll area
  // (minus its bottom padding), so the page fills the screen with no scroll.
  // A final check trims any overflow left over from layout we can't see.
  const cardRef = useRef(null);
  const [cardHeight, setCardHeight] = useState(null);
  useLayoutEffect(() => {
    const findScroller = (node) => {
      for (let p = node.parentElement; p; p = p.parentElement) {
        const oy = getComputedStyle(p).overflowY;
        if (oy === 'auto' || oy === 'scroll') return p;
      }
      return document.scrollingElement || document.documentElement;
    };
    const measure = () => {
      const el = cardRef.current;
      if (!el) return;
      const scroller = findScroller(el);
      const isRoot = scroller === document.scrollingElement || scroller === document.documentElement;
      const bottom = isRoot ? window.innerHeight : scroller.getBoundingClientRect().bottom;
      const padB = isRoot ? 0 : parseFloat(getComputedStyle(scroller).paddingBottom) || 0;
      let height = Math.max(Math.floor(bottom - padB - el.getBoundingClientRect().top), 0);
      el.style.height = height + 'px';
      const overflow = scroller.scrollHeight - scroller.clientHeight;
      if (overflow > 0) {
        height = Math.max(height - overflow, 0);
        el.style.height = height + 'px';
      }
      setCardHeight(height);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [isMobile]);

  const dashed = isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.14)';
  const tileBg = isDark ? 'transparent' : '#f8fafc';
  const badge = {
    fontSize: isMobile ? 12 : 13, fontWeight: 700, padding: isMobile ? '3px 8px' : '4px 10px', borderRadius: 7, whiteSpace: 'nowrap',
    background: palette.warningSoft, color: isDark ? palette.warning : '#b45309',
  };

  // Ghost versions of the real salary stats (Net / Gross / Insurance).
  const items = [
    { key: 'net', icon: Award, label: t.missingNet },
    { key: 'gross', icon: DollarSign, label: t.missingGross },
    { key: 'insurance', icon: Shield, label: t.missingInsurance },
  ];

  return (
    <div style={{ ...styles.container, marginTop: '-30px' }}>
      <SessionStyles palette={palette} />
      <div style={styles.backgroundElements}><div style={styles.circle1} /><div style={styles.circle2} /></div>

      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Page header — keeps the page recognisable */}
        <div style={styles.card1}>
          <h1 className="nx-page-title" style={styles.headerTitle}>{t.pageTitle}</h1>
          <p style={styles.headerSubtitle}>{t.pageSubtitle}</p>
        </div>

        <section
          ref={cardRef}
          role="alert"
          dir={isRTL ? 'rtl' : 'ltr'}
          style={{
            width: '100%', boxSizing: 'border-box',
            height: cardHeight ?? 'auto', minHeight: 0, overflowY: 'auto',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: isMobile ? '22px 16px' : 28,
            borderRadius: isMobile ? 16 : 20,
            background: palette.surface, border: `1px solid ${palette.border}`,
          }}
        >
          <div style={{ width: '100%', maxWidth: 780, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
            <IconTile icon={Wallet} tone="warning" palette={palette} size={isMobile ? 56 : 76} />

            <h2 style={{ margin: isMobile ? '18px 0 8px' : '24px 0 10px', fontSize: isMobile ? 20 : 30, fontWeight: 800, lineHeight: 1.35, color: palette.text }}>
              {t.missingDataTitle}
            </h2>
            <p style={{ margin: 0, maxWidth: 600, fontSize: isMobile ? 14.5 : 17, lineHeight: 1.65, color: palette.textSecondary }}>
              {t.missingDataError}
            </p>

            {isMobile ? (
              // Phones: tiles collapse into a compact list
              <div style={{ width: '100%', marginTop: 18, borderRadius: 12, border: `1px dashed ${dashed}`, background: tileBg, textAlign: 'start' }}>
                {items.map(({ key, icon, label }, i) => (
                  <div key={key} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
                    borderTop: i === 0 ? 'none' : `1px dashed ${dashed}`,
                  }}>
                    <IconTile icon={icon} tone="accent" palette={palette} size={30} />
                    <div style={{ flex: 1, fontSize: 14, fontWeight: 600, color: palette.text }}>{label}</div>
                    <span style={badge}>{t.notSet}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{
                width: '100%', marginTop: 32, display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 16, textAlign: 'start',
              }}>
                {items.map(({ key, icon, label }) => (
                  <div key={key} style={{
                    display: 'flex', flexDirection: 'column', gap: 12, padding: '20px 22px',
                    borderRadius: 16, border: `1px dashed ${dashed}`, background: tileBg,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <IconTile icon={icon} tone="accent" palette={palette} size={42} />
                      <span style={badge}>{t.notSet}</span>
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: palette.textSecondary }}>{label}</div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: palette.muted }}>— {t.egp}</div>
                  </div>
                ))}
              </div>
            )}

            {/* What to do */}
            <div style={{
              width: '100%', boxSizing: 'border-box', marginTop: isMobile ? 12 : 20,
              display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', gap: 12,
              padding: isMobile ? '12px 14px' : '16px 20px', borderRadius: isMobile ? 12 : 14,
              background: palette.accentSoft, border: `1px solid ${palette.accentBorder}`, textAlign: 'start',
            }}>
              <UserCheck size={isMobile ? 18 : 22} color={palette.accentText} style={{ flexShrink: 0 }} aria-hidden="true" />
              <div style={{ fontSize: isMobile ? 13.5 : 15.5, lineHeight: 1.6, color: palette.text }}>{t.missingDataHint}</div>
            </div>

            <button
              type="button"
              className="nx-session-btn nx-session-btn--secondary"
              onClick={() => window.location.reload()}
              style={{ marginTop: isMobile ? 16 : 28, width: isMobile ? '100%' : 'auto', minHeight: 46, padding: '0 24px', fontSize: 15 }}
            >
              <RefreshCw size={16} aria-hidden="true" />
              {t.refresh}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default MissingSalaryState;
